import type { LocaleCode } from "./locales";

/** ruTorrent language codes keyed by dashboard locale. ruTorrent reads its own
 * `Language` cookie (plain code, path `/`), so the dashboard mirrors its active
 * language into that cookie to keep both surfaces in the same language. */
export const RUTORRENT_LANGUAGE_BY_LOCALE: Record<LocaleCode, string> = {
  en: "en",
  de: "de",
  da: "da",
  es: "es",
  fr: "fr",
  "pt-BR": "pt-br",
  "zh-CN": "zh-cn",
};

/** Returns the ruTorrent code for a dashboard locale, or `undefined` when the
 * locale has no ruTorrent equivalent. An unmapped locale carries nothing. */
export const mapLocaleToRutorrent = (locale: string): string | undefined =>
  (RUTORRENT_LANGUAGE_BY_LOCALE as Record<string, string>)[locale];

const RUTORRENT_LANGUAGE_COOKIE = "Language";
/** Records the code the dashboard last synced, so a later ruTorrent-side change
 * (which updates `Language` but not this marker) can be told apart from a cookie
 * the dashboard itself wrote. */
const RUTORRENT_SYNC_COOKIE = "qb_lang_sync";
const RUTORRENT_LANGUAGE_MAX_AGE_DAYS = 365;

type CookieIO = {
  getCookie: (name: string) => string | undefined;
  setCookie: (name: string, value: string, days: number) => void;
};

/** Mirrors the active dashboard language into ruTorrent's `Language` cookie, with
 * a companion `qb_lang_sync` marker so the dashboard stays the authority.
 *
 * On each authenticated settle the dashboard writes both cookies when `Language`
 * is absent, when the marker is absent (never synced), or when `Language` equals
 * the marker (ruTorrent unchanged since the last sync). It leaves both alone only
 * when `Language` differs from the marker, which means a language was chosen
 * inside ruTorrent after the last sync. A dashboard language change always writes
 * both. `reset` clears the in-session change tracking so a new identity is routed
 * through the cookie rules rather than treated as a change. */
export function createRutorrentLanguageCarry(io: CookieIO) {
  let carried: LocaleCode | undefined;
  const writeBoth = (code: string) => {
    io.setCookie(RUTORRENT_LANGUAGE_COOKIE, code, RUTORRENT_LANGUAGE_MAX_AGE_DAYS);
    io.setCookie(RUTORRENT_SYNC_COOKIE, code, RUTORRENT_LANGUAGE_MAX_AGE_DAYS);
  };
  return {
    carry(locale: LocaleCode): void {
      const code = mapLocaleToRutorrent(locale);
      if (!code) return;
      if (carried === locale) return;
      const isChange = carried !== undefined;
      carried = locale;
      if (isChange) {
        writeBoth(code);
        return;
      }
      const current = io.getCookie(RUTORRENT_LANGUAGE_COOKIE);
      const marker = io.getCookie(RUTORRENT_SYNC_COOKIE);
      if (current === undefined || marker === undefined || current === marker) {
        writeBoth(code);
      }
    },
    reset(): void {
      carried = undefined;
    },
  };
}

export type RutorrentLanguageCarry = ReturnType<typeof createRutorrentLanguageCarry>;
